import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getEntityHeader, mapApiEntityHeaderToUi } from '../../../services/entityHeader.js';
import { apiErrorStatus } from './apiErrorStatus.js';

/**
 * The identification record behind one entity.
 *
 * Called ONCE by the profile shell and handed to the tabs through outlet
 * context. A tab calling this for itself would re-request the same header on
 * every tab switch, which is both wasteful and visible as a flicker.
 *
 * `notFound` and `accessDenied` are separate flags rather than one error
 * because they are separate screens: "this record does not exist" and "you may
 * not see this record" are different statements, and showing the first when the
 * second is true misinforms the user about their colleague's data.
 */
export function useEntityHeader({ entityType, entityId } = {}) {
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const fetchIdRef = useRef(0);
  // The record open NOW. The page stays mounted across records, so a `reload`
  // captured for the previous one (a workflow started there that answers late,
  // CT-5713) must neither paint that record nor cancel this one's request.
  const recordKey = `${entityType}/${entityId}`;
  const openRecordRef = useRef(recordKey);

  // `quiet` re-reads the header after a change made elsewhere (a workflow
  // started from a Next Best Action, CT-5713) without flashing the skeleton.
  const fetchHeader = useCallback(async ({ quiet = false } = {}) => {
    if (!entityType || !entityId) return;
    if (`${entityType}/${entityId}` !== openRecordRef.current) return;
    const requestId = ++fetchIdRef.current;
    if (!quiet) {
      setLoading(true);
      setError(null);
      setStatus(null);
    }

    try {
      const { body } = await getEntityHeader({ entityType, entityId });
      if (requestId !== fetchIdRef.current) return;
      setEntity(mapApiEntityHeaderToUi(body));
    } catch (err) {
      if (requestId !== fetchIdRef.current || quiet) return;
      setEntity(null);
      setStatus(apiErrorStatus(err));
      setError(err?.message ?? 'Failed to load the entity');
    } finally {
      if (requestId === fetchIdRef.current) setLoading(false);
    }
  }, [entityType, entityId]);

  const refresh = useCallback(() => fetchHeader(), [fetchHeader]);
  const reload = useCallback(() => fetchHeader({ quiet: true }), [fetchHeader]);

  // Declared BEFORE the fetch effect, so the new record is "open" when it loads.
  useEffect(() => {
    openRecordRef.current = recordKey;
  }, [recordKey]);

  useEffect(() => {
    fetchHeader();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchHeader]);

  return useMemo(
    () => ({
      entity,
      loading,
      error,
      notFound: status === 404,
      accessDenied: status === 403,
      refresh,
      reload,
    }),
    [entity, loading, error, status, refresh, reload],
  );
}

export default useEntityHeader;
